import type { ClassData, Group, Student } from "../../types/models";
import { validateConstraints, mergeTransitiveMustTogether } from "../constraints";
import { processTagGrouping } from "./tags";
import { greedyPlacement } from "./placement";
import { hillClimbingOptimization } from "./optimization";
import { loadBiasPreferences, processBiasConstraints, mergeConstraints, ensureBiasAdjacency } from "./bias";
import { chooseColor } from "../../utils/colors";
import { createSeededRandom } from "../random";

export interface ProgressHook {
    (label: string, pct: number): void;
}

/**
 * Main group generation pipeline
 * Supports optional seeded randomness for reproducible results
 * Supports per-tag balance/group behavior
 * Supports locked students who won't be moved
 */
export function generateGroups(
    cls: ClassData,
    opts: { onProgress?: ProgressHook } = {}
): Group[] {
    const onProgress = opts.onProgress ?? (() => {});

    // Create seeded RNG if seed is provided
    const rng = cls.settings.seed ? createSeededRandom(cls.settings.seed) : undefined;

    // Get present students
    const present: Student[] = cls.students.filter(s => !s.absent);

    if (present.length === 0) {
        return [];
    }

    // STAGE 0: Process hidden bias preferences (probabilistic constraints)
    // Teacher constraints override bias preferences
    const biasPrefs = loadBiasPreferences();
    const presentStudents = present.map(s => ({ id: s.id, name: s.name }));
    const { mustTogether, mustApart, triggeredPairs } = processBiasConstraints(biasPrefs, presentStudents, cls.constraints);
    const effectiveConstraints = mergeConstraints(cls.constraints, { mustTogether, mustApart });

    // STAGE 1: Validate constraints (including bias-generated ones)
    onProgress("Validating constraints", 10);
    const validation = validateConstraints(present, effectiveConstraints, cls.settings);
    if (!validation.ok) {
        throw new Error(validation.errors.join("; "));
    }

    // STAGE 2: Initialize groups
    onProgress("Initializing groups", 20);

    // Calculate group count that satisfies size constraints
    const minViableGroups = Math.ceil(present.length / cls.settings.maxSize);
    const maxViableGroups = Math.floor(present.length / cls.settings.minSize);
    const idealGroups = Math.ceil(present.length / Math.max(1, cls.settings.targetSize));

    // Choose group count closest to ideal while satisfying constraints
    let targetGroups = idealGroups;
    if (targetGroups > maxViableGroups) {
        // Too many groups - some would be below minSize
        targetGroups = maxViableGroups;
    } else if (targetGroups < minViableGroups) {
        // Too few groups - some would exceed maxSize
        targetGroups = minViableGroups;
    }

    // Apply maxGroups cap if specified
    if (cls.settings.maxGroups !== undefined && cls.settings.maxGroups > 0) {
        targetGroups = Math.min(targetGroups, cls.settings.maxGroups);
    }

    const groups: Group[] = Array.from({ length: Math.max(1, targetGroups) }, (_, i) => ({
        id: `g${i + 1}`,
        memberIds: [],
        color: chooseColor(i)
    }));

    // STAGE 2.5: Place locked students first
    onProgress("Placing locked students", 25);
    const lockedStudents = present.filter(s => s.lockedToGroupId);
    for (const student of lockedStudents) {
        const targetGroup = groups.find(g => g.id === student.lockedToGroupId);
        if (targetGroup && !targetGroup.memberIds.includes(student.id)) {
            targetGroup.memberIds.push(student.id);
        }
    }

    // STAGE 3: Place must-together sets (including bias-generated)
    onProgress("Placing must-together groups", 30);
    const forcedSets = mergeTransitiveMustTogether(effectiveConstraints);

    // Get IDs of already-placed students (locked)
    const alreadyPlaced = new Set(groups.flatMap(g => g.memberIds));

    for (const set of forcedSets) {
        // Filter out already-placed students from the set
        const unplacedInSet = set.ids.filter(id => !alreadyPlaced.has(id));
        if (unplacedInSet.length === 0) continue;

        // Find first group with capacity
        const targetGroup = groups.find(g =>
            g.memberIds.length + unplacedInSet.length <= cls.settings.maxSize
        );

        if (targetGroup) {
            targetGroup.memberIds.push(...unplacedInSet);
            unplacedInSet.forEach(id => alreadyPlaced.add(id));
        } else {
            // Shouldn't happen if validation passed
            throw new Error(`Cannot fit must-together set of ${unplacedInSet.length} students`);
        }
    }

    // STAGE 4: Process tag grouping
    onProgress("Processing tag grouping", 45);
    processTagGrouping(present, groups, cls.settings);

    // STAGE 5: Greedy placement
    onProgress("Placing students", 60);
    greedyPlacement(present, groups, effectiveConstraints, cls.settings, rng);

    // STAGE 6: Hill climbing optimization
    onProgress("Optimizing groups", 75);
    hillClimbingOptimization(groups, present, effectiveConstraints, cls.settings, 3);

    // STAGE 6.5: Validate and redistribute undersized groups
    onProgress("Validating group sizes", 90);

    // Find groups below minSize
    const undersized = groups.filter(g =>
        g.memberIds.length > 0 &&
        g.memberIds.length < cls.settings.minSize
    );

    // Redistribute students from undersized groups
    for (const smallGroup of undersized) {
        const studentsToMove = [...smallGroup.memberIds];

        for (const studentId of studentsToMove) {
            // Find a recipient group that can accept without exceeding maxSize
            const recipient = groups.find(g =>
                g.id !== smallGroup.id &&
                g.memberIds.length < cls.settings.maxSize
            );

            if (recipient) {
                // Move student
                recipient.memberIds.push(studentId);
                smallGroup.memberIds = smallGroup.memberIds.filter(id => id !== studentId);
            } else {
                // No recipient available - this is an impossible configuration
                // This shouldn't happen if Step 1 is correct, but handle gracefully
                console.warn(`Cannot redistribute student ${studentId} - no group has capacity`);
            }
        }
    }

    // STAGE 7: Cleanup
    onProgress("Finalizing", 95);
    const nonEmpty = groups.filter(g => g.memberIds.length > 0);

    // Final validation: warn if any groups violate constraints
    for (const group of nonEmpty) {
        if (group.memberIds.length < cls.settings.minSize) {
            console.warn(`Group ${group.id} has ${group.memberIds.length} students (below minSize ${cls.settings.minSize})`);
        }
        if (group.memberIds.length > cls.settings.maxSize) {
            console.warn(`Group ${group.id} has ${group.memberIds.length} students (above maxSize ${cls.settings.maxSize})`);
        }
    }

    // STAGE 8: Ensure bias-paired students are adjacent in group lists
    ensureBiasAdjacency(nonEmpty, triggeredPairs);

    onProgress("Done", 100);

    return nonEmpty;
}

